
import React, { useState, useEffect } from 'react';
import { LogEntry, ScopeAnalysis, WbsItem } from '../types';
import { generateCoDraft } from '../services/geminiService';

interface CoDraftModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceLog: LogEntry;
  analysis: ScopeAnalysis;
  wbsContext: WbsItem[];
  onSaveDraft: (data: any) => void;
}

const CoDraftModal: React.FC<CoDraftModalProps> = ({ onClose, sourceLog, analysis, wbsContext, onSaveDraft }) => {
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<{ title: string, summary: string, narrative: string, suggestedImpactDays?: number } | null>(null);
  const [laborCost, setLaborCost] = useState(0);
  const [materialCost, setMaterialCost] = useState(0);

  useEffect(() => {
    const fetchDraft = async () => {
      setLoading(true);
      try {
        const result = await generateCoDraft(sourceLog, analysis, wbsContext);
        setDraft(result);
      } catch (err) {
        console.error("Draft failed", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDraft();
  }, [sourceLog, analysis, wbsContext]);

  return (
    <div className="fixed inset-0 bg-white/90 backdrop-blur-xl flex items-center justify-center z-50 p-6">
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-2xl w-full max-w-6xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-10 py-8 flex justify-between items-center border-b border-slate-50">
          <div>
            <h2 className="text-2xl font-light tracking-tight text-slate-900">Change Order Protocol</h2>
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Defensive Documentation Hub</p>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-black transition-colors p-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex flex-1 overflow-hidden">
          {/* Narrative Editor */}
          <div className="flex-1 px-10 py-8 overflow-y-auto space-y-8">
            {loading ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-300 py-20">
                <div className="w-8 h-8 border-2 border-slate-100 border-t-slate-900 rounded-full animate-spin mb-4"></div>
                <p className="text-[10px] font-black tracking-[0.2em] uppercase opacity-40">Drafting Forensic Evidence</p>
              </div>
            ) : (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-700">
                <div className="space-y-1.5">
                  <label className="block text-[8px] font-black text-slate-300 uppercase tracking-[0.2em]">Title</label>
                  <input 
                    value={draft?.title || ''}
                    onChange={(e) => setDraft(d => d ? {...d, title: e.target.value} : null)}
                    className="w-full bg-white border-b border-slate-100 py-1.5 text-lg font-medium focus:border-slate-900 outline-none transition-all placeholder:text-slate-200"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[8px] font-black text-slate-300 uppercase tracking-[0.2em]">Forensic Narrative</label>
                  <textarea 
                    value={draft?.narrative || ''}
                    onChange={(e) => setDraft(d => d ? {...d, narrative: e.target.value} : null)}
                    rows={10}
                    className="w-full bg-slate-50/50 rounded-2xl px-5 py-4 text-xs leading-relaxed focus:ring-1 focus:ring-slate-200 outline-none resize-none placeholder:text-slate-300 font-light text-slate-700"
                  />
                </div>

                <div className="grid grid-cols-2 gap-8">
                  <div className="space-y-1.5">
                    <label className="block text-[8px] font-black text-slate-300 uppercase tracking-[0.2em]">Labor Impact ($)</label>
                    <input 
                      type="number"
                      value={laborCost}
                      onChange={(e) => setLaborCost(Number(e.target.value))}
                      className="w-full bg-white border-b border-slate-100 py-1.5 text-base font-light focus:border-slate-900 outline-none transition-all"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[8px] font-black text-slate-300 uppercase tracking-[0.2em]">Material Impact ($)</label>
                    <input 
                      type="number"
                      value={materialCost}
                      onChange={(e) => setMaterialCost(Number(e.target.value))}
                      className="w-full bg-white border-b border-slate-100 py-1.5 text-base font-light focus:border-slate-900 outline-none transition-all"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Reference Sidebar */}
          <div className="w-80 bg-slate-50/30 border-l border-slate-50 p-8 space-y-8 overflow-y-auto shrink-0">
             <div className="space-y-3">
               <h4 className="text-[8px] font-black text-slate-300 uppercase tracking-widest">Field Source</h4>
               <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                 <p className="text-[10px] text-slate-500 leading-relaxed font-light italic line-clamp-4">"{sourceLog.text}"</p>
               </div>
             </div>

             <div className="space-y-3">
               <h4 className="text-[8px] font-black text-slate-300 uppercase tracking-widest">Contract Basis</h4>
               <div className="space-y-2">
                 {wbsContext.map(w => (
                   <div key={w.id} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                     <p className="text-[9px] font-bold mb-0.5">{w.code}</p>
                     <p className="text-[9px] text-slate-400 font-medium truncate">{w.title}</p>
                   </div>
                 ))}
               </div>
             </div>

             <div className="p-6 bg-slate-900 rounded-[1.5rem] text-white/90">
               <h4 className="text-[8px] font-black uppercase tracking-[0.1em] text-slate-500 mb-2">Forensic Logic</h4>
               <p className="text-[10px] leading-relaxed font-light opacity-80">
                 Referencing Section <span className="text-white font-medium">{wbsContext[0]?.code || 'N/A'}</span> establishes the contract baseline. Frame discovery as variance from WBS.
               </p>
             </div>
          </div>
        </div>

        <div className="px-10 py-8 bg-white border-t border-slate-50 flex gap-4">
          <button onClick={onClose} className="flex-1 py-4 text-slate-400 text-[10px] font-black uppercase tracking-widest hover:text-black transition-colors">Discard</button>
          <button 
            disabled={loading}
            onClick={() => onSaveDraft(draft)}
            className="flex-1 py-4 bg-black text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl hover:bg-slate-800 transition-all disabled:opacity-20"
          >
            Finalize Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoDraftModal;
